
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IIdentityIntegrationPortalPin,
  IIdentityIntegrationPortalPinSummary,
} from '@/services/identityIntegrationsService';

const MAPPED_FIELDS = ['pin'];

/**
 * The per-Integration access code for the H2 developer portal — see
 * sp-identity ADR-0015.
 *
 * Revoking access means clearing the PIN; there is no expiry and no disable
 * flag. Sessions already open at the portal outlive it, which the confirmation
 * says out loud rather than leaving to be discovered.
 */
@Component({
  name: 'PortalAccessCard',
  components: { SimpleConfirmationDialog },
})
export default class PortalAccessCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected input: string = '';
  protected revealed: string = '';
  protected busy: boolean = false;
  protected errors: IApiFieldError[] = [];
  protected regenerateDialog: boolean = false;
  protected submitDialog: boolean = false;
  protected revokeDialog: boolean = false;
  protected pinDialog: boolean = false;

  /**
   * Only a DIFFERENT integration invalidates a revealed PIN.
   *
   * The parent reassigns this prop and refetches after every write, so
   * clearing on any change wiped the PIN a moment after it was shown — the
   * reveal appeared to do nothing at all. Switching to another integration
   * must still drop it, which is what the id comparison is for.
   */
  @Watch('integration')
  onIntegrationChanged(next: IIdentityIntegration, previous: IIdentityIntegration | null) {
    this.errors = [];

    if (!previous || next.id !== previous.id) {
      this.closePinDialog();
    }
  }

  get portalPin(): IIdentityIntegrationPortalPinSummary {
    return this.integration.portal_pin;
  }

  get hasInput(): boolean {
    return this.input.trim() !== '';
  }

  get stateLabel(): string {
    if (!this.portalPin.configured) {
      return this.$t('identityIntegrations.portalAccess.missing') as string;
    }
    if (!this.portalPin.usable) {
      return this.$t('identityIntegrations.portalAccess.unreadable') as string;
    }
    return this.$t('identityIntegrations.portalAccess.configured') as string;
  }

  get stateClass(): string {
    if (!this.portalPin.configured) {
      return 'ii-pin-state--warn';
    }
    return this.portalPin.usable ? 'ii-pin-state--ok' : 'ii-pin-state--error';
  }

  get confirmReplacementBody(): string {
    return this.portalPin.configured
      ? (this.$t('identityIntegrations.portalAccess.confirmReplaceBody') as string)
      : (this.$t('identityIntegrations.portalAccess.confirmFirstBody') as string);
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, MAPPED_FIELDS);
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.errors, field);
  }

  async reveal() {
    // A read changes nothing, so it tells the parent nothing. Emitting here
    // made the detail view refetch and re-render this card for no reason.
    await this.run(() => identityIntegrationsService.portalPin(this.integration.id), undefined, undefined, false);
  }

  async regenerate() {
    this.regenerateDialog = false;

    await this.run(
      () => identityIntegrationsService.regeneratePortalPin(this.integration.id),
      'identityIntegrations.portalAccess.regenerated'
    );
  }

  async submit() {
    this.submitDialog = false;

    if (!this.hasInput) {
      return;
    }

    const pin = this.input.trim();

    await this.run(
      () => identityIntegrationsService.replacePortalPin(this.integration.id, pin),
      'identityIntegrations.portalAccess.replaced',
      () => { this.input = ''; }
    );
  }

  async revoke() {
    this.revokeDialog = false;

    await this.run(
      () => identityIntegrationsService.clearPortalPin(this.integration.id),
      'identityIntegrations.portalAccess.revoked'
    );
  }

  private async run(
    call: () => Promise<IIdentityIntegrationPortalPin>,
    successMessage?: string,
    onSuccess?: () => void,
    notifyParent: boolean = true
  ) {
    this.busy = true;
    this.errors = [];
    try {
      const result = await call();

      // A PIN that was just set is shown straight away: this is the only moment
      // it can be read without a second, deliberate reveal, and whoever pressed
      // the button is exactly who needs it.
      this.revealed = result.pin || '';
      this.pinDialog = this.revealed !== '';

      if (onSuccess) {
        onSuccess();
      }

      if (successMessage) {
        this.$uiBus.emit('info', this.$t(successMessage) as string);
      }

      if (notifyParent) {
        this.$emit('updated', {
          ...this.integration,
          portal_pin: { configured: result.configured, usable: result.usable },
        });
      }
    } catch (error) {
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  copy() {
    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(this.revealed).then(() => {
      this.$uiBus.emit('info', this.$t('identityIntegrations.portalAccess.copied') as string);
    });
  }

  copyAndClose() {
    this.copy();
    this.closePinDialog();
  }

  /**
   * Closing forgets the PIN as well as hiding the dialog. Keeping it in
   * component state after the operator dismissed it would leave a credential
   * sitting in memory for the rest of the page's life, and the button reopens
   * it by asking the API again — which is also what puts the second look in the
   * access log.
   */
  closePinDialog() {
    this.pinDialog = false;
    this.revealed = '';
  }
}
